"use client";

import { useState, useEffect, useRef } from "react";
import { Spinner, Card, CardHeader, CardBody } from "reactstrap";
import { jwtDecode } from "jwt-decode";
import { toast } from "react-toastify";
import { useAppDispatch, useAppSelector } from "../../../../ReduxToolkit/Hooks";
import {
  setExhibition,
  syncFromStorage,
} from "../../../../ReduxToolkit/Reducer/ExhibitionSlice";
import ExhibitionSwitcherService from "../../../../Service/ExhibitionSwitcherService";
import { setActiveExhibition, type StoredExhibition } from "../../../../utils/exhibitionContext";

interface DecodedToken {
  is_superuser: boolean;
}

function isSuperUser(): boolean {
  try {
    const token = localStorage.getItem("accessToken");
    if (!token) return false;
    const decoded = jwtDecode<DecodedToken>(token);
    return decoded.is_superuser === true;
  } catch {
    return false;
  }
}

/** Format "2026-04-10" → "Apr 2026" */
function fmtShort(iso: string): string {
  return new Date(iso).toLocaleDateString(undefined, {
    year: "numeric",
    month: "short",
  });
}

const ExhibitionSelector = () => {
  const dispatch = useAppDispatch();
  const activeExhibition = useAppSelector((s) => s.exhibition.active);

  const [open, setOpen]       = useState(false);
  const [loading, setLoading] = useState(false);
  const [list, setList]       = useState<StoredExhibition[]>([]);
  const [superUser]           = useState(isSuperUser);
  const dropdownRef           = useRef<HTMLLIElement>(null);

  // Sync Redux from localStorage on mount (handles page refresh)
  useEffect(() => {
    dispatch(syncFromStorage());
  }, [dispatch]);

  // Re-sync when another tab / component writes to localStorage
  useEffect(() => {
    const onStorageChange = () => dispatch(syncFromStorage());
    window.addEventListener("exhibitionChanged", onStorageChange);
    return () => window.removeEventListener("exhibitionChanged", onStorageChange);
  }, [dispatch]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const openDropdown = async () => {
    if (open) { setOpen(false); return; }
    setOpen(true);
    if (list.length > 0) return;

    setLoading(true);
    try {
      const exhibitions = superUser
        ? await ExhibitionSwitcherService.getAllExhibitions()
        : await ExhibitionSwitcherService.getActiveExhibitions();
      setList(exhibitions);
    } catch {
      toast.error("Could not load exhibitions.");
      setOpen(false);
    } finally {
      setLoading(false);
    }
  };

  const handleSwitch = (ex: StoredExhibition) => {
    dispatch(setExhibition(ex));
    setActiveExhibition(ex);
    setOpen(false);
    window.location.reload();
  };

  const today  = new Date().toISOString().slice(0, 10);
  const label  = activeExhibition ? activeExhibition.title : "No Exhibition";
  const isActive = (ex: StoredExhibition) => activeExhibition?.id === ex.id;

  return (
    <li
      className="onhover-dropdown"
      style={{ listStyle: "none", position: "relative" }}
      ref={dropdownRef}
    >
      {/* ── Trigger button (lives in the dark header — keep white-tinted styles) ── */}
      <button
        onClick={openDropdown}
        title="Switch exhibition"
        style={{
          display: "flex",
          alignItems: "center",
          gap: "6px",
          background: "rgba(255,255,255,0.08)",
          border: "1px solid rgba(255,255,255,0.18)",
          borderRadius: "8px",
          padding: "5px 12px",
          cursor: "pointer",
          color: "inherit",
          fontSize: "0.8rem",
          fontWeight: 600,
          transition: "background 0.15s",
          maxWidth: 220,
          whiteSpace: "nowrap",
          overflow: "hidden",
          textOverflow: "ellipsis",
        }}
      >
        <span style={{ fontSize: "1rem" }}>🏛️</span>
        <span style={{ overflow: "hidden", textOverflow: "ellipsis", maxWidth: 150 }}>
          {label}
        </span>
        <span style={{ opacity: 0.6, fontSize: "0.65rem", marginLeft: 2 }}>
          {open ? "▲" : "▼"}
        </span>
      </button>

      {/* ── Dropdown ── uses Card so the theme controls background + text color ── */}
      {open && (
        <div
          style={{
            position: "absolute",
            right: 0,
            top: "calc(100% + 8px)",
            minWidth: 280,
            maxWidth: 340,
            zIndex: 9999,
            borderRadius: 12,
            overflow: "hidden",
            boxShadow: "0 8px 32px rgba(0,0,0,0.18)",
          }}
        >
          <Card className="mb-0" style={{ border: "1px solid rgba(0,0,0,0.1)", borderRadius: 12, overflow: "hidden" }}>

            {/* Header */}
            <CardHeader
              style={{
                padding: "10px 14px",
                fontSize: "0.75rem",
                textTransform: "uppercase",
                letterSpacing: "0.08em",
                fontWeight: 700,
                opacity: 0.6,
              }}
            >
              {superUser ? "All Exhibitions" : "Active Exhibitions"}
            </CardHeader>

            {/* List */}
            <CardBody style={{ padding: 0, maxHeight: 320, overflowY: "auto" }}>
              {loading ? (
                <div className="text-center p-4">
                  <Spinner size="sm" />
                  <span className="ms-2" style={{ fontSize: "0.8rem", opacity: 0.7 }}>
                    Loading…
                  </span>
                </div>
              ) : list.length === 0 ? (
                <div className="text-center p-4" style={{ opacity: 0.5, fontSize: "0.85rem" }}>
                  No exhibitions found.
                </div>
              ) : (
                list.map((ex) => {
                  const current  = isActive(ex);
                  const running  = ex.start_date <= today && today <= ex.end_date;
                  const upcoming = ex.start_date > today;
                  const closed   = !ex.status;

                  return (
                    <button
                      key={ex.id}
                      onClick={() => handleSwitch(ex)}
                      style={{
                        display: "block",
                        width: "100%",
                        textAlign: "left",
                        padding: "10px 14px",
                        background: current ? "rgba(0,102,102,0.08)" : "transparent",
                        border: "none",
                        borderBottom: "1px solid rgba(0,0,0,0.06)",
                        cursor: "pointer",
                        color: "inherit",
                        transition: "background 0.12s",
                      }}
                      onMouseEnter={(e) =>
                        !current &&
                        ((e.currentTarget as HTMLButtonElement).style.background = "rgba(0,0,0,0.04)")
                      }
                      onMouseLeave={(e) =>
                        !current &&
                        ((e.currentTarget as HTMLButtonElement).style.background = "transparent")
                      }
                    >
                      {/* Title row */}
                      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 2 }}>
                        <span
                          style={{
                            fontWeight: 600,
                            fontSize: "0.85rem",
                            color: current ? "#006666" : "inherit",
                          }}
                        >
                          {current && "✓ "}
                          {ex.title}
                        </span>

                        {/* Status pill */}
                        <span
                          style={{
                            fontSize: "0.65rem",
                            fontWeight: 700,
                            padding: "2px 7px",
                            borderRadius: 20,
                            ...(closed
                              ? { background: "rgba(239,68,68,0.12)", color: "#dc2626", border: "1px solid rgba(239,68,68,0.3)" }
                              : running
                              ? { background: "rgba(0,102,102,0.12)", color: "#006666", border: "1px solid rgba(0,102,102,0.3)" }
                              : upcoming
                              ? { background: "rgba(217,119,6,0.12)", color: "#b45309", border: "1px solid rgba(217,119,6,0.3)" }
                              : { background: "rgba(100,116,139,0.12)", color: "#475569", border: "1px solid rgba(100,116,139,0.3)" }),
                          }}
                        >
                          {closed ? "Closed" : running ? "Running" : upcoming ? "Upcoming" : "Active"}
                        </span>
                      </div>

                      {/* Dates */}
                      <div style={{ fontSize: "0.73rem", opacity: 0.55 }}>
                        📅 {fmtShort(ex.start_date)} – {fmtShort(ex.end_date)}
                        {ex.venue && ` · ${ex.venue}`}
                      </div>
                    </button>
                  );
                })
              )}
            </CardBody>

            {/* Footer note for superusers */}
            {superUser && (
              <div
                style={{
                  padding: "8px 14px",
                  borderTop: "1px solid rgba(0,0,0,0.08)",
                  fontSize: "0.7rem",
                  opacity: 0.45,
                  textAlign: "center",
                }}
              >
                Superuser — all exhibitions visible
              </div>
            )}
          </Card>
        </div>
      )}
    </li>
  );
};

export default ExhibitionSelector;